import React, { useState } from 'react'
import { startOfMonth, isFuture, startOfDay } from 'date-fns'
import { useIntl } from 'react-intl'
import type { StyleProp } from '@vtex/admin-ui'
import {
  Flex,
  experimental_useDatePickerState as useDatePickerState,
  experimental_DatePickerField as DatePickerField,
  experimental_DatePickerCalendar as DatePickerCalendar,
  experimental_I18nProvider as I18nProvider,
  csx,
} from '@vtex/admin-ui'
import { useFormState } from '@vtex/admin-ui-form'

import { SalesPeriod } from './SalesPeriod'
import type { SalesPeriodButtonProps } from './SalesPeriodButton'
import { FieldErrorText } from './FieldErrorText'
import { useUpdateEffect } from '../../../../../../core/hooks/useUpdateEffect'

const calendarStyle: StyleProp = {
  zIndex: 11,
  '>div': { padding: '$space-2', paddingTop: '$space-3' },
  'header ::first-letter': { textTransform: 'uppercase' },
  th: { paddingBottom: '1.5rem' },
  'td>button': {
    width: '2.7rem',
    height: '2.7rem',
    color: 'black',
  },
  'td[aria-selected="true"]>button': {
    borderRadius: 'circle',
    backgroundColor: '$blue40',
    color: 'white',
  },
}

export const useSalesPeriodPicker = (initialState?: SalesPeriod) => {
  return useState(
    () =>
      initialState ??
      SalesPeriod.create({ start: startOfMonth(new Date()), end: new Date() })
  )
}

export type SalesPeriodPickerOptions = {
  state: UseSalesPeriodPickerStateReturn
}

export type SalesPeriodPickerProps = Omit<
  SalesPeriodButtonProps,
  'value' | 'label' | keyof SalesPeriodPickerOptions
> &
  SalesPeriodPickerOptions

export type UseSalesPeriodPickerStateReturn = ReturnType<
  typeof useSalesPeriodPickerState
>

export const useSalesPeriodPickerState = (defaultValues: SalesPeriod) => {
  const intl = useIntl()

  const form = useFormState<SalesPeriod>({
    defaultValues,
  })

  const isFutureDate = (date: Date) => {
    if (isFuture(startOfDay(new Date(date)))) {
      return intl.formatMessage({
        id: 'sales-performance.sales-period-picker.future-error',
      })
    }

    return true
  }

  form.register('start', {
    valueAsDate: true,
    validate: isFutureDate,
  })

  form.register('end', {
    valueAsDate: true,
    validate: isFutureDate,
  })

  return { form, defaultValues }
}

export const SalesPeriodPicker = ({
  state: { form, defaultValues },
}: SalesPeriodPickerProps) => {
  const intl = useIntl()
  const { errors } = form.formState

  const startState = useDatePickerState({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    defaultValue: {
      year: defaultValues.start.getFullYear(),
      month: defaultValues.start.getMonth(),
      day: defaultValues.start.getDate(),
    },
  })

  const endState = useDatePickerState({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    defaultValue: {
      year: defaultValues.end.getFullYear(),
      month: defaultValues.end.getMonth(),
      day: defaultValues.end.getDate(),
    },
  })

  useUpdateEffect(
    function onStartChange() {
      const startDate = new Date(
        startState.dateValue.year,
        startState.dateValue.month,
        startState.dateValue.day
      )

      form.setValue('start', startDate, { shouldValidate: true })

      if (startDate > form.getValues('end')) {
        endState.setDateValue(startState.dateValue)
        form.setValue('end', startDate, {
          shouldValidate: true,
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startState.dateValue]
  )

  useUpdateEffect(
    function onEndChange() {
      const endDate = new Date(
        endState.dateValue.year,
        endState.dateValue.month,
        endState.dateValue.day
      )

      form.setValue('end', endDate, { shouldValidate: true })

      if (endDate < form.getValues('start')) {
        startState.setDateValue(endState.dateValue)
        form.setValue('start', endDate, {
          shouldValidate: true,
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endState.dateValue]
  )

  return (
    <Flex
      direction="column"
      align="stretch"
      className={csx({ gap: '$space-3' })}
    >
      <I18nProvider locale={intl.locale}>
        <div>
          <DatePickerField
            label={intl.formatMessage({
              id: 'sales-performance.sales-period-picker.start-period',
            })}
            state={startState}
            className={csx({ '>div': { width: 'full' } })}
          />
          <DatePickerCalendar
            className={csx(calendarStyle)}
            state={startState}
          />
          {errors.start && <FieldErrorText value={errors.start} />}
        </div>

        <div>
          <DatePickerField
            label={intl.formatMessage({
              id: 'sales-performance.sales-period-picker.end-period',
            })}
            state={endState}
            className={csx({ '>div': { width: 'full' } })}
          />
          <DatePickerCalendar className={csx(calendarStyle)} state={endState} />
          {errors.end && <FieldErrorText value={errors.end} />}
        </div>
      </I18nProvider>
    </Flex>
  )
}
